import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { appointmentsAPI, holidaysAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { toast } from 'sonner';
import { 
  AlertCircle, 
  CheckCircle2, 
  Clock, 
  Calendar as CalendarIcon,
  ArrowRight,
  ArrowLeft,
  Info,
  ChevronRight
} from 'lucide-react';
import { de } from 'date-fns/locale';
import { format, addMonths, startOfMonth, endOfMonth, isMonday, isTuesday, isWednesday, isThursday, isBefore, startOfDay } from 'date-fns';

export default function Booking() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [duration, setDuration] = useState('15');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [nextAvailableDate, setNextAvailableDate] = useState(null);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    street: user?.street || '',
    postal_code: user?.postal_code || '',
    city: user?.city || '',
    description: '',
  });
  const [bookingResult, setBookingResult] = useState(null);
  const calendarRef = useRef(null);

  // Fetch holidays
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    Promise.all([
      holidaysAPI.getByYear(currentYear),
      holidaysAPI.getByYear(currentYear + 1)
    ]).then(([res1, res2]) => {
      const allHolidays = [...res1.data, ...res2.data].map(h => new Date(h.date));
      setHolidays(allHolidays);
    }).catch(err => console.error('Failed to load holidays:', err));
  }, []);

  // Fetch available slots when month changes
  const fetchSlots = async (month, isInitialLoad = false) => {
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    
    try {
      const response = await appointmentsAPI.getSlots(
        format(start, 'yyyy-MM-dd'),
        format(end, 'yyyy-MM-dd')
      );
      
      // Filter out past dates
      const today = startOfDay(new Date());
      const futureSlots = response.data.filter(slot => {
        const slotDate = new Date(slot.date);
        return !isBefore(slotDate, today);
      });
      
      setSlots(futureSlots);
      
      // On initial load, check if there are available slots in this month
      if (isInitialLoad) {
        const availableSlots = futureSlots.filter(s => s.available);
        if (availableSlots.length === 0) {
          // No available slots in current month, search next months
          await findNextAvailableMonth(addMonths(month, 1), 6);
        } else {
          // Set first available date
          const firstAvailable = new Date(availableSlots[0].date);
          setNextAvailableDate(firstAvailable);
        }
        setInitialLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setInitialLoading(false);
    }
  };

  // Search for the next month with available slots
  const findNextAvailableMonth = async (startMonth, maxMonthsToSearch) => {
    for (let i = 0; i < maxMonthsToSearch; i++) {
      const searchMonth = addMonths(startMonth, i);
      const start = startOfMonth(searchMonth);
      const end = endOfMonth(searchMonth);
      
      try {
        const response = await appointmentsAPI.getSlots(
          format(start, 'yyyy-MM-dd'),
          format(end, 'yyyy-MM-dd')
        );
        
        const availableSlots = response.data.filter(s => s.available);
        if (availableSlots.length > 0) {
          // Found available slots! Navigate calendar to this month
          const firstAvailable = new Date(availableSlots[0].date);
          setCalendarMonth(searchMonth);
          setSlots(response.data);
          setNextAvailableDate(firstAvailable);
          return;
        }
      } catch (error) {
        console.error('Error searching month:', error);
      }
    }
    
    // No slots found in next 6 months
    setNextAvailableDate(null);
  };

  // Navigate to next available date
  const goToNextAvailable = () => {
    if (nextAvailableDate) {
      setCalendarMonth(startOfMonth(nextAvailableDate));
      fetchSlots(startOfMonth(nextAvailableDate));
    }
  };

  useEffect(() => {
    fetchSlots(new Date(), true);
  }, []);

  // Pre-fill form if user is logged in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        street: user.street || '',
        postal_code: user.postal_code || '',
        city: user.city || '',
      }));
    }
  }, [user]);

  const isValidBookingDay = (date) => {
    // Only Monday to Thursday
    return isMonday(date) || isTuesday(date) || isWednesday(date) || isThursday(date);
  };

  const isHoliday = (date) => {
    return holidays.some(h => 
      h.getDate() === date.getDate() && 
      h.getMonth() === date.getMonth() && 
      h.getFullYear() === date.getFullYear()
    );
  };

  const getAvailableTimesForDate = (date) => {
    if (!date) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    const today = startOfDay(new Date());
    
    // Filter slots for the selected date that are available and in the future
    return slots.filter(s => {
      if (s.date !== dateStr || !s.available) return false;
      const slotDate = new Date(s.date);
      return !isBefore(slotDate, today);
    });
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const bookingData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        street: formData.street,
        postal_code: formData.postal_code,
        city: formData.city,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        duration: parseInt(duration),
        description: formData.description || null,
      };

      let response;
      if (isAuthenticated) {
        response = await appointmentsAPI.create({
          date: bookingData.date,
          time: bookingData.time,
          duration: bookingData.duration,
          description: bookingData.description,
        });
      } else {
        response = await appointmentsAPI.createGuest(bookingData);
      }

      setBookingResult(response.data);
      setStep(4);
      toast.success('Ihr Termin wurde erfolgreich gebucht. Sie erhalten eine Bestätigungs-E-Mail.');
    } catch (error) {
      console.error('Booking failed:', error);
      // Handle slot_taken gracefully - refresh slots and go back
      if (error.response?.status === 409 || error.response?.data?.detail === 'slot_taken') {
        toast.info('Dieser Zeitslot wurde gerade vergeben. Bitte wählen Sie einen anderen Termin.');
        // Refresh available slots
        fetchSlots(selectedDate);
        setSelectedTime(null);
        setStep(2);
      } else {
        // Log error internally but show friendly message
        toast.info('Bitte versuchen Sie es erneut oder wählen Sie einen anderen Termin.');
        setStep(2);
      }
    } finally {
      setLoading(false);
    }
  };

  const availableTimes = selectedDate ? getAvailableTimesForDate(selectedDate) : [];

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* Important Notice */}
        <Card className="mb-8 border-[#2E2A88] bg-[#2E2A88]/5" data-testid="booking-notice">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <Info className="w-6 h-6 text-[#2E2A88] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-barlow font-bold text-lg text-[#2E2A88] mb-2">Wichtiger Hinweis</h3>
                <ul className="font-manrope text-sm text-gray-700 space-y-1">
                  <li>• Termine nur <strong>Montag bis Donnerstag</strong> zwischen <strong>15:00 und 16:00 Uhr</strong></li>
                  <li>• Termindauer: <strong>15–30 Minuten</strong> vor Ort</li>
                  <li>• Der Termin ist <strong>kostenlos und unverbindlich</strong></li>
                </ul>
                <div className="mt-4 p-3 bg-white rounded-lg border border-[#9B1B30]/20">
                  <p className="font-manrope text-sm text-[#9B1B30] font-medium">
                    Sie finden keinen passenden Termin? Kein Problem!
                  </p>
                  <p className="font-manrope text-sm text-gray-600 mt-1">
                    Rufen Sie uns an unter <a href="tel:+4921337787688" className="font-bold text-[#9B1B30] hover:underline">02133 7787688</a> und wir richten uns nach Ihnen.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-md mx-auto">
            {['Datum', 'Zeit', 'Daten', 'Fertig'].map((label, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-barlow font-bold text-sm ${
                  step > index + 1 
                    ? 'bg-[#10B981] text-white' 
                    : step === index + 1 
                      ? 'bg-[#9B1B30] text-white' 
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > index + 1 ? <CheckCircle2 className="w-5 h-5" /> : index + 1}
                </div>
                <span className={`font-manrope text-xs mt-2 ${
                  step === index + 1 ? 'text-[#9B1B30] font-medium' : 'text-gray-500'
                }`}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Date Selection */}
        {step === 1 && (
          <Card className="animate-fadeIn" data-testid="step-date">
            <CardHeader>
              <CardTitle className="font-barlow text-2xl flex items-center gap-3">
                <CalendarIcon className="w-6 h-6 text-[#9B1B30]" />
                Wählen Sie einen Tag
              </CardTitle>
            </CardHeader>
            <CardContent>
              {initialLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-12 h-12 border-4 border-[#9B1B30] border-t-transparent rounded-full animate-spin mb-4" />
                  <p className="font-manrope text-gray-600">Verfügbare Termine werden geladen...</p>
                </div>
              ) : (
                <>
                  {/* Show hint if viewing current month with no availability */}
                  {nextAvailableDate && slots.filter(s => s.available).length === 0 && (
                    <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Info className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <p className="font-manrope text-sm text-amber-800">
                          In diesem Monat sind keine Termine mehr verfügbar.
                        </p>
                      </div>
                      <Button
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={goToNextAvailable}
                        data-testid="go-to-next-available-btn"
                      >
                        Nächster Termin
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      locale={de}
                      month={calendarMonth}
                      onMonthChange={(month) => {
                        setCalendarMonth(month);
                        fetchSlots(month);
                      }}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return (
                          date < today ||
                          !isValidBookingDay(date) ||
                          isHoliday(date)
                        );
                      }}
                      className="rounded-md border"
                      classNames={{
                        day_selected: "bg-[#9B1B30] text-white hover:bg-[#7A1525]",
                        day_today: "bg-[#2E2A88]/10 text-[#2E2A88] font-bold",
                      }}
                      data-testid="booking-calendar"
                    />
                  </div>
                  
                  <div className="mt-6 flex justify-end">
                    <Button 
                      className="btn-primary"
                      disabled={!selectedDate}
                      onClick={() => setStep(2)}
                      data-testid="step1-next-btn"
                    >
                      Weiter
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Time Selection */}
        {step === 2 && (
          <Card className="animate-fadeIn" data-testid="step-time">
            <CardHeader>
              <CardTitle className="font-barlow text-2xl flex items-center gap-3">
                <Clock className="w-6 h-6 text-[#9B1B30]" />
                Wählen Sie eine Uhrzeit
              </CardTitle>
              <p className="font-manrope text-gray-600">
                {selectedDate && format(selectedDate, "EEEE, d. MMMM yyyy", { locale: de })}
              </p>
            </CardHeader>
            <CardContent>
              {availableTimes.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {availableTimes.map((slot) => (
                    <Button
                      key={slot.time}
                      variant={selectedTime === slot.time ? 'default' : 'outline'}
                      className={`h-14 font-barlow font-bold ${
                        selectedTime === slot.time 
                          ? 'bg-[#9B1B30] hover:bg-[#7A1525]' 
                          : 'hover:border-[#9B1B30] hover:text-[#9B1B30]'
                      }`}
                      onClick={() => setSelectedTime(slot.time)}
                      data-testid={`time-slot-${slot.time}`}
                    >
                      {slot.time} Uhr
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="font-manrope text-gray-600">
                    Keine freien Termine an diesem Tag verfügbar.
                  </p>
                </div>
              )}

              <div className="mt-6">
                <Label className="font-barlow font-bold text-sm mb-3 block">Termindauer</Label>
                <RadioGroup value={duration} onValueChange={setDuration} className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="15" id="d15" data-testid="duration-15" />
                    <Label htmlFor="d15" className="font-manrope cursor-pointer">15 Minuten</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="30" id="d30" data-testid="duration-30" />
                    <Label htmlFor="d30" className="font-manrope cursor-pointer">30 Minuten</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="mt-8 flex justify-between">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  data-testid="step2-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <Button 
                  className="btn-primary"
                  disabled={!selectedTime}
                  onClick={() => setStep(3)}
                  data-testid="step2-next-btn"
                >
                  Weiter
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Contact Form */}
        {step === 3 && (
          <Card className="animate-fadeIn" data-testid="step-form">
            <CardHeader>
              <CardTitle className="font-barlow text-2xl">Ihre Kontaktdaten</CardTitle>
              <p className="font-manrope text-gray-600">
                {selectedDate && format(selectedDate, "EEEE, d. MMMM yyyy", { locale: de })} um {selectedTime} Uhr
              </p>
            </CardHeader>
            <CardContent>
              <form className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name" className="font-barlow font-bold text-sm">Vorname *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="mt-1"
                      required
                      data-testid="input-firstname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="font-barlow font-bold text-sm">Nachname *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="mt-1"
                      required
                      data-testid="input-lastname"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="font-barlow font-bold text-sm">E-Mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1"
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="font-barlow font-bold text-sm">Mobilnummer *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1"
                      required
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="street" className="font-barlow font-bold text-sm">Straße & Hausnummer *</Label>
                  <AddressAutocomplete
                    value={formData.street}
                    onChange={(val) => setFormData({ ...formData, street: val })}
                    onAddressSelect={(addr) => {
                      setFormData({
                        ...formData,
                        street: addr.street,
                        postal_code: addr.postcode || formData.postal_code,
                        city: addr.city || formData.city
                      });
                    }}
                    placeholder="Straße und Hausnummer eingeben..."
                    className="mt-1 [&_input]:bg-white [&_input]:border-gray-300 [&_input]:text-gray-900 [&_input]:placeholder-gray-400"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code" className="font-barlow font-bold text-sm">PLZ *</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      className="mt-1"
                      required
                      data-testid="input-plz"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city" className="font-barlow font-bold text-sm">Ort *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="mt-1"
                      required
                      data-testid="input-city"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description" className="font-barlow font-bold text-sm">Anmerkungen (optional)</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1"
                    placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                    rows={4}
                    data-testid="input-description"
                  />
                </div>
              </form>

              <div className="mt-8 flex justify-between">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(2)}
                  data-testid="step3-back-btn"
                  disabled={loading}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <Button 
                  className="btn-primary"
                  disabled={
                    loading ||
                    !formData.first_name ||
                    !formData.last_name ||
                    !formData.email ||
                    !formData.phone ||
                    !formData.street ||
                    !formData.postal_code ||
                    !formData.city
                  }
                  onClick={handleSubmit}
                  data-testid="step3-submit-btn"
                >
                  {loading ? 'Wird gebucht...' : 'Termin buchen'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Success */}
        {step === 4 && bookingResult && (
          <Card className="animate-fadeIn text-center" data-testid="step-success">
            <CardContent className="py-12">
              <div className="w-20 h-20 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
              </div>
              <h2 className="font-barlow font-bold text-2xl text-[#1A1A1A] mb-2">
                Termin erfolgreich gebucht!
              </h2>
              <p className="font-manrope text-gray-600 mb-8">
                Sie erhalten in Kürze eine Bestätigung per E-Mail.
              </p>

              <div className="bg-[#F8F9FA] rounded-xl p-6 max-w-md mx-auto mb-8">
                <div className="space-y-3 text-left">
                  <div className="flex justify-between">
                    <span className="font-manrope text-gray-600">Kundennummer:</span>
                    <span className="font-barlow font-bold text-[#9B1B30]">{bookingResult.customer_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-manrope text-gray-600">Datum:</span>
                    <span className="font-manrope font-medium">
                      {format(new Date(bookingResult.date), "d. MMMM yyyy", { locale: de })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-manrope text-gray-600">Uhrzeit:</span>
                    <span className="font-manrope font-medium">{bookingResult.time} Uhr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-manrope text-gray-600">Dauer:</span>
                    <span className="font-manrope font-medium">{bookingResult.duration} Minuten</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {!isAuthenticated && (
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/register')}
                    data-testid="success-register-btn"
                  >
                    Konto erstellen
                  </Button>
                )}
                <Button 
                  className="btn-primary"
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
                  data-testid="success-home-btn"
                >
                  {isAuthenticated ? 'Zum Dashboard' : 'Zur Startseite'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
